import React, { useState, useEffect, useCallback } from 'react';
import { partnershipsAPI } from '../services/api';
import { FiRefreshCw, FiAlertTriangle, FiBriefcase, FiPlus, FiX, FiTrash2 } from 'react-icons/fi';
import './Students.css';
import './StudentModal.css';
import './ClientVertical.css';

const emptyForm = {
  university: '',
  commission_tier_percent: '',
  discount_percent: '',
  min_committed_volume: '',
  is_exclusive: false,
  term_start: '',
  term_end: '',
  notes: '',
};

const PartnershipModal = ({ universities, onSave, onCancel, loading }) => {
  const [form, setForm] = useState(emptyForm);
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 480 }}>
        <div className="modal-header"><h2 style={{ fontSize: 18 }}>New University Partnership</h2></div>
        <div className="modal-form">
          <div className="form-group">
            <label>University *</label>
            <select name="university" value={form.university} onChange={handleChange}>
              <option value="">Select a university…</option>
              {universities.map((u) => (
                <option key={u.id} value={u.id}>{u.name} ({u.country})</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Commission tier % (we receive)</label>
              <input type="number" name="commission_tier_percent" min="0" max="100" step="0.5" value={form.commission_tier_percent} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Tuition discount % (we offer)</label>
              <input type="number" name="discount_percent" min="0" max="100" step="0.5" value={form.discount_percent} onChange={handleChange} />
            </div>
          </div>

          <div className="form-group">
            <label>Committed enrollments this term</label>
            <input type="number" name="min_committed_volume" min="0" value={form.min_committed_volume} onChange={handleChange} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Term start</label>
              <input type="date" name="term_start" value={form.term_start} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Term end</label>
              <input type="date" name="term_end" value={form.term_end} onChange={handleChange} />
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#333', fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" name="is_exclusive" checked={form.is_exclusive} onChange={handleChange} style={{ width: 'auto' }} />
            Exclusive / primary channel deal
          </label>

          <div className="form-group">
            <label>Notes</label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Contract details, contacts…" />
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
          <button
            className="btn-primary"
            onClick={() => onSave({
              ...form,
              university: form.university ? parseInt(form.university, 10) : null,
              commission_tier_percent: form.commission_tier_percent || null,
              discount_percent: form.discount_percent || null,
              min_committed_volume: form.min_committed_volume ? parseInt(form.min_committed_volume, 10) : null,
              term_start: form.term_start || null,
              term_end: form.term_end || null,
            })}
            disabled={loading || !form.university}
          >
            {loading ? 'Saving…' : 'Save Partnership'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ProgressBar = ({ percent }) => {
  if (percent === null || percent === undefined) return <span style={{ color: '#94a3b8' }}>—</span>;
  const clamped = Math.min(100, percent);
  const color = percent >= 100 ? '#43e97b' : percent >= 50 ? '#4facfe' : '#f5a623';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 130 }}>
      <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${clamped}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ color, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{percent}%</span>
    </div>
  );
};

const Partnerships = () => {
  const [partnerships, setPartnerships] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pRes, uRes] = await Promise.all([
        partnershipsAPI.list(),
        partnershipsAPI.universities().catch(() => ({ data: { results: [] } })),
      ]);
      setPartnerships(pRes.data.results || pRes.data || []);
      setUniversities(uRes.data.results || uRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load partnerships.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    setActionLoading(true);
    try {
      await partnershipsAPI.create(data);
      setShowModal(false);
      load();
    } catch (e) {
      setError(JSON.stringify(e?.response?.data) || 'Failed to save partnership.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete the ${p.university_name} partnership record?`)) return;
    try {
      await partnershipsAPI.delete(p.id);
      load();
    } catch (e) {
      setError('Failed to delete partnership.');
    }
  };

  return (
    <div className="students-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiBriefcase size={22} color="#011ff3" />
            University Partnerships
          </h1>
          <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 14 }}>
            Negotiated volume deals — delivered vs committed enrollments per term, for renegotiation leverage.
          </p>
        </div>
        <div className="header-actions-right">
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            <FiPlus size={15} /> New Partnership
          </button>
          <button className="btn-icon" onClick={load} title="Refresh">
            <FiRefreshCw size={15} className={loading ? 'cv-spin' : ''} />
          </button>
        </div>
      </div>

      {universities.length === 0 && !loading && (
        <div style={{ background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.3)', borderRadius: 10, padding: '12px 18px', color: '#b45309', fontSize: 13 }}>
          The university catalog is empty. Add universities in the Django admin (/admin/ → Universities) before creating partnerships.
        </div>
      )}

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <FiAlertTriangle size={18} />
          <span style={{ flex: 1, wordBreak: 'break-word' }}>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#c33', cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      <div className="students-table-container">
        {loading ? (
          <div className="empty-state">Loading…</div>
        ) : partnerships.length === 0 ? (
          <div className="empty-state">No partnerships yet.</div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                {['University', 'Commission %', 'Discount %', 'Committed', 'Delivered', 'Progress', 'Term', 'Exclusive', ''].map((h) => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {partnerships.map((p) => (
                <tr key={p.id} style={{ opacity: p.is_active ? 1 : 0.5 }}>
                  <td>
                    <strong>{p.university_name}</strong>
                    <div style={{ color: '#94a3b8', fontSize: 11 }}>{p.university_country}</div>
                  </td>
                  <td>{p.commission_tier_percent ? `${parseFloat(p.commission_tier_percent)}%` : '—'}</td>
                  <td>{p.discount_percent ? `${parseFloat(p.discount_percent)}%` : '—'}</td>
                  <td>{p.min_committed_volume ?? '—'}</td>
                  <td style={{ fontWeight: 600 }}>{p.enrolled_delivered}</td>
                  <td><ProgressBar percent={p.volume_progress_percent} /></td>
                  <td style={{ color: '#94a3b8', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {p.term_start || '…'} → {p.term_end || '…'}
                  </td>
                  <td>
                    {p.is_exclusive ? (
                      <span className="status-pill" style={{ color: '#011ff3' }}>Exclusive</span>
                    ) : '—'}
                  </td>
                  <td>
                    <button onClick={() => handleDelete(p)} title="Delete" className="btn-icon"><FiTrash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <PartnershipModal universities={universities} onSave={handleSave} onCancel={() => setShowModal(false)} loading={actionLoading} />
      )}
    </div>
  );
};

export default Partnerships;
